import type {
  User,
  AdminUser,
  AdminReprocessResult,
  Match,
  MatchListResponse,
  Collection,
  CollectionListResponse,
  CollectionMatchesResponse,
  Server,
  CaptureSession,
  MatchCorrectionRequest,
  ActiveMatchCorrection,
  MatchCorrectionStatus,
  DeletedMatch,
  CollectionActivityEntry,
  DeletedMatchDetails,
  WorldCupSummary,
  WorldCupMatch
} from './types'

class ApiError extends Error {
  status: number

  constructor(message: string, status: number) {
    super(message)
    this.status = status
    this.name = 'ApiError'
  }
}

async function request<T>(method: string, url: string, body?: unknown): Promise<T> {
  const options: RequestInit = {
    method,
    credentials: 'include',
    headers: {}
  }

  if (body) {
    options.headers = { 'Content-Type': 'application/json' }
    options.body = JSON.stringify(body)
  }

  let response: Response
  try {
    response = await fetch(url, options)
  } catch (err) {
    throw new ApiError('Cannot connect to server', 0)
  }

  if (response.status === 401) {
    throw new ApiError('Not authenticated', 401)
  }

  if (!response.ok) {
    let message: string
    try {
      const data = await response.json()
      message = data.detail || 'Request failed'
    } catch {
      message = `Request failed: ${response.status}`
    }
    throw new ApiError(message, response.status)
  }

  const contentType = response.headers.get('content-type')
  if (contentType?.includes('application/json')) {
    return response.json()
  }

  return response as unknown as T
}

// HTTP method shortcuts
const get = <T>(url: string) => request<T>('GET', url)
const post = <T>(url: string, body?: unknown) => request<T>('POST', url, body)
const put = <T>(url: string, body?: unknown) => request<T>('PUT', url, body)
const del = <T>(url: string, body?: unknown) => request<T>('DELETE', url, body)

export const api = {
  auth: {
    async login(username: string, password: string): Promise<{ message: string }> {
      const params = new URLSearchParams()
      params.append('username', username)
      params.append('password', password)

      let response: Response
      try {
        response = await fetch('/api/auth/login', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: params
        })
      } catch (err) {
        throw new ApiError('Cannot connect to server', 0)
      }

      if (!response.ok) {
        let message = 'Login failed'
        try {
          const data = await response.json()
          message = data.detail || message
        } catch {
          // Response wasn't JSON
        }
        throw new ApiError(message, response.status)
      }

      // Check if response has content
      const contentType = response.headers.get('content-type')
      if (contentType?.includes('application/json')) {
        return response.json()
      }

      return { message: 'Login successful' }
    },

    async logout(): Promise<void> {
      await post('/api/auth/logout')
    },

    async me(): Promise<User> {
      return get<User>('/api/auth/me')
    }
  },

  servers: {
    async list(): Promise<Server[]> {
      return get<Server[]>('/api/servers/')
    }
  },

  matches: {
    async list(params?: { page?: number; per_page?: number; search?: string; owner?: string; match_type?: string; collection?: string; sort?: string; sort_order?: 'asc' | 'desc' }): Promise<Match[] | MatchListResponse> {
      const queryParams = new URLSearchParams()
      if (params?.page) queryParams.set('page', String(params.page))
      if (params?.per_page) queryParams.set('per_page', String(params.per_page))
      if (params?.search) queryParams.set('search', params.search)
      if (params?.owner) queryParams.set('owner', params.owner)
      if (params?.match_type) queryParams.set('match_type', params.match_type)
      if (params?.collection) queryParams.set('collection', params.collection)
      if (params?.sort) queryParams.set('sort', params.sort)
      if (params?.sort_order) queryParams.set('sort_order', params.sort_order)
      const query = queryParams.toString()
      return get<Match[] | MatchListResponse>(`/api/matches/${query ? '?' + query : ''}`)
    },

    async get(matchId: string): Promise<Response> {
      const response = await fetch(`/api/matches/${matchId}`, {
        credentials: 'include'
      })

      if (response.status === 401) {
        throw new ApiError('Not authenticated', 401)
      }

      if (!response.ok) {
        throw new ApiError('Failed to load match', response.status)
      }

      return response
    },

    async rename(matchId: string, name: string): Promise<void> {
      await put(`/api/matches/${matchId}/rename`, { name })
    },

    async updateMetadata(matchId: string, data: Partial<{
      custom_name: string
      blue_team_name: string
      red_team_name: string
      match_type: string
      published: boolean
    }>): Promise<void> {
      await put(`/api/matches/${matchId}/metadata`, data)
    },

    async delete(matchId: string): Promise<void> {
      await del(`/api/matches/${matchId}`)
    },

    async getCorrections(matchId: string): Promise<MatchCorrectionStatus> {
      return get<MatchCorrectionStatus>(`/api/matches/${matchId}/corrections`)
    },

    async requestCorrection(matchId: string, data: {
      type: 'void_goal' | 'ignore_player'
      reason: string
      goal_t?: number
      player?: string
    }): Promise<{ message: string; request: MatchCorrectionRequest }> {
      return post(`/api/matches/${matchId}/correction-requests`, data)
    },

    async getMetadata(matchId: string): Promise<{
      match_id: string
      owner: string
      server_name?: string
      blue_team_name?: string
      red_team_name?: string
      color_overrides?: Record<string, string>
    }> {
      return get(`/api/matches/${matchId}/metadata`)
    },

  },

  capture: {
    async sessions(): Promise<CaptureSession[]> {
      return get<CaptureSession[]>('/api/capture/sessions')
    },

    async connect(config: {
      server_url: string
      server_name: string
      bot_name: string
      team: string
      password?: string | null
      auto_record?: boolean
    }): Promise<CaptureSession> {
      return post<CaptureSession>('/api/capture/start', config)
    },

    async disconnect(sessionId: string): Promise<void> {
      await post(`/api/capture/${sessionId}/stop`)
    },

    async delete(sessionId: string): Promise<void> {
      await del(`/api/capture/${sessionId}`)
    },

  },

  admin: {
    async getUsers(): Promise<{ users: AdminUser[] }> {
      return get<{ users: AdminUser[] }>('/api/admin/users')
    },

    async restartCapture(): Promise<{ message: string }> {
      return post<{ message: string }>('/api/admin/restart-capture')
    },

    async createUser(username: string, role: string): Promise<{ generated_password: string }> {
      return post<{ generated_password: string }>('/api/admin/users', { username, role })
    },

    async updateUserRole(username: string, role: string): Promise<void> {
      await put(`/api/admin/users/${encodeURIComponent(username)}/role`, { role })
    },

    async resetPassword(username: string): Promise<{ generated_password: string }> {
      return post<{ generated_password: string }>(`/api/admin/users/${encodeURIComponent(username)}/reset-password`)
    },

    async deleteUser(username: string): Promise<void> {
      await del(`/api/admin/users/${encodeURIComponent(username)}`)
    },

    async getCorrectionRequests(statusFilter = 'pending'): Promise<{ requests: MatchCorrectionRequest[] }> {
      return get(`/api/admin/correction-requests?status_filter=${encodeURIComponent(statusFilter)}`)
    },

    async getActiveCorrections(): Promise<{ corrections: ActiveMatchCorrection[] }> {
      return get('/api/admin/active-corrections')
    },

    async approveCorrection(requestId: string, note = ''): Promise<void> {
      await post(`/api/admin/correction-requests/${requestId}/approve`, { note })
    },

    async rejectCorrection(requestId: string, note = ''): Promise<void> {
      await post(`/api/admin/correction-requests/${requestId}/reject`, { note })
    },

    async restoreCorrection(correctionId: string, note = ''): Promise<void> {
      await del(`/api/admin/active-corrections/${correctionId}`, { note })
    },

    async getDeletedMatches(): Promise<{ matches: DeletedMatch[]; total: number }> {
      return get('/api/admin/deleted-matches')
    },

    async getDeletedMatchDetails(matchId: string): Promise<DeletedMatchDetails> {
      return get(`/api/admin/deleted-matches/${encodeURIComponent(matchId)}/details`)
    },

    async restoreDeletedMatch(matchId: string): Promise<void> {
      await post(`/api/admin/deleted-matches/${encodeURIComponent(matchId)}/restore`)
    },

    async permanentlyDeleteMatch(matchId: string): Promise<void> {
      await del(`/api/admin/deleted-matches/${encodeURIComponent(matchId)}`)
    },

    async getCollectionActivity(limit = 200): Promise<{ activity: CollectionActivityEntry[]; total: number }> {
      return get(`/api/admin/collection-activity?limit=${limit}`)
    },

    async downloadBackup(): Promise<Response> {
      const response = await fetch('/api/admin/backup', {
        credentials: 'include'
      })

      if (response.status === 401) {
        throw new ApiError('Not authenticated', 401)
      }

      if (!response.ok) {
        let message: string
        try {
          const data = await response.json()
          message = data.detail || 'Failed to download backup'
        } catch {
          message = `Request failed: ${response.status}`
        }
        throw new ApiError(message, response.status)
      }

      return response
    },

    async reprocessAllStats(): Promise<AdminReprocessResult> {
      return post<AdminReprocessResult>('/api/admin/reprocess')
    },
  },


  worldCup: {
    async summary(): Promise<WorldCupSummary> {
      return get<WorldCupSummary>('/api/world-cup')
    },

    async saveMatchReview(matchId: string, data: {
      blue_team: string
      red_team: string
      goals: Array<{ key: string; scorer: string; assist: string }>
      stats_verified?: boolean
    }): Promise<WorldCupMatch> {
      return put<WorldCupMatch>(`/api/world-cup/matches/${encodeURIComponent(matchId)}/review`, data)
    },

    async unverifyMatch(matchId: string): Promise<WorldCupMatch> {
      return del<WorldCupMatch>(`/api/world-cup/matches/${encodeURIComponent(matchId)}/review`)
    }
  },

  collections: {
    async list(params?: {
      page?: number
      per_page?: number
      sort?: string
      order?: string
      search?: string
      include_previews?: boolean
    }): Promise<CollectionListResponse> {
      const queryParams = new URLSearchParams()
      if (params?.page) queryParams.set('page', String(params.page))
      if (params?.per_page) queryParams.set('per_page', String(params.per_page))
      if (params?.sort) queryParams.set('sort', params.sort)
      if (params?.order) queryParams.set('order', params.order)
      if (params?.search) queryParams.set('search', params.search)
      if (params?.include_previews !== undefined) queryParams.set('include_previews', String(params.include_previews))
      const query = queryParams.toString()
      return get<CollectionListResponse>(`/api/collections/${query ? '?' + query : ''}`)
    },

    async get(slug: string): Promise<Collection> {
      return get<Collection>(`/api/collections/${slug}`)
    },

    async getMatches(slug: string, params?: {
      page?: number
      per_page?: number
      sort?: string
      order?: string
      search?: string
    }): Promise<CollectionMatchesResponse> {
      const queryParams = new URLSearchParams()
      if (params?.page) queryParams.set('page', String(params.page))
      if (params?.per_page) queryParams.set('per_page', String(params.per_page))
      if (params?.sort) queryParams.set('sort', params.sort)
      if (params?.order) queryParams.set('order', params.order)
      if (params?.search) queryParams.set('search', params.search)
      const query = queryParams.toString()
      return get<CollectionMatchesResponse>(`/api/collections/${slug}/matches${query ? '?' + query : ''}`)
    },

    async create(data: {
      name: string
      description?: string
      slug?: string
    }): Promise<Collection> {
      return post<Collection>('/api/collections/', data)
    },

    async update(slug: string, data: Partial<Collection>): Promise<Collection> {
      return put<Collection>(`/api/collections/${slug}`, data)
    },

    async delete(slug: string): Promise<void> {
      await del(`/api/collections/${slug}`)
    },

    async addMatch(slug: string, matchId: string): Promise<void> {
      await post(`/api/collections/${slug}/matches`, { match_id: matchId })
    },

    async removeMatch(slug: string, matchId: string): Promise<void> {
      await del(`/api/collections/${slug}/matches/${matchId}`)
    },

    async addMatches(slug: string, matchIds: string[]): Promise<{ added: number }> {
      return post<{ added: number }>(`/api/collections/${slug}/matches/batch`, { match_ids: matchIds })
    },

    async removeMatches(slug: string, matchIds: string[]): Promise<{ removed: number }> {
      return del<{ removed: number }>(`/api/collections/${slug}/matches/batch`, { match_ids: matchIds })
    }
  }
}

export { ApiError }
export type { User, AdminUser, Match, Collection, Server, CaptureSession }
